import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listPutawayRules, getPutawayRefs, createPutawayRule, togglePutawayRule, deletePutawayRule } from "@/lib/admin-config.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/admin/putaway")({ component: PutawayRulesAdmin });

function PutawayRulesAdmin() {
  const qc = useQueryClient();
  const { tenantId } = useScope();

  const fetchRules = useServerFn(listPutawayRules);
  const fetchRefs = useServerFn(getPutawayRefs);
  const createFn = useServerFn(createPutawayRule);
  const toggleFn = useServerFn(togglePutawayRule);
  const deleteFn = useServerFn(deletePutawayRule);

  const [open, setOpen] = useState(false);
  const [customerId, setCustomerId] = useState<string>("");
  const [skuId, setSkuId] = useState<string>("");
  const [zone, setZone] = useState("");
  const [locationId, setLocationId] = useState<string>("");
  const [priority, setPriority] = useState("100");
  const [notes, setNotes] = useState("");

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ["putaway-rules", tenantId],
    enabled: !!tenantId,
    queryFn: () => runServerFn(() => fetchRules({ data: { tenantId: tenantId! } }), "Loading rules"),
  });

  const { data: refs } = useQuery({
    queryKey: ["putaway-refs", tenantId],
    enabled: !!tenantId,
    queryFn: () => runServerFn(() => fetchRefs({ data: { tenantId: tenantId! } }), "Loading references"),
  });
  const customers = refs?.customers ?? [];
  const skus = refs?.skus ?? [];
  const locations = refs?.locations ?? [];

  const customerName = (id: string | null) => customers.find((c: any) => c.id === id)?.name ?? "—";
  const skuLabel = (id: string | null) => {
    const s = skus.find((x: any) => x.id === id);
    return s ? `${s.sku_code} · ${s.name}` : "—";
  };
  const locationCode = (id: string | null) => locations.find((l: any) => l.id === id)?.code ?? "—";

  const reset = () => {
    setCustomerId(""); setSkuId(""); setZone(""); setLocationId(""); setPriority("100"); setNotes("");
  };

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    if (!skuId && !customerId && !zone && !locationId) {
      return toast.error("Pick at least one match (SKU, customer, zone, or location)");
    }
    try {
      await runServerFn(() => createFn({ data: {
        tenantId,
        customerId: customerId || null,
        skuId: skuId || null,
        zone: zone.trim() || null,
        locationId: locationId || null,
        priority: Number(priority) || 100,
        notes: notes.trim() || null,
      } }), "Adding rule");
      toast.success("Rule added");
      setOpen(false); reset();
      qc.invalidateQueries({ queryKey: ["putaway-rules", tenantId] });
    } catch { /* */ }
  };

  const toggleActive = async (id: string, active: boolean) => {
    try {
      await runServerFn(() => toggleFn({ data: { id, active: !active } }), "Updating rule");
      qc.invalidateQueries({ queryKey: ["putaway-rules", tenantId] });
    } catch { /* */ }
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this rule?")) return;
    try {
      await runServerFn(() => deleteFn({ data: { id } }), "Deleting rule");
      qc.invalidateQueries({ queryKey: ["putaway-rules", tenantId] });
    } catch { /* */ }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Putaway Rules</h2>
          <p className="text-sm text-muted-foreground">
            Direct incoming inventory to the right slot. Lower priority numbers win.
          </p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
          <DialogTrigger asChild>
            <Button disabled={!tenantId}><Plus className="h-4 w-4 mr-2" />New rule</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add putaway rule</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Customer (optional)</Label>
                  <Select value={customerId || "any"} onValueChange={(v) => setCustomerId(v === "any" ? "" : v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any customer</SelectItem>
                      {customers.map((c: any) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>SKU (optional)</Label>
                  <Select value={skuId || "any"} onValueChange={(v) => setSkuId(v === "any" ? "" : v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      <SelectItem value="any">Any SKU</SelectItem>
                      {skus.map((s: any) => <SelectItem key={s.id} value={s.id}>{s.sku_code} · {s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Zone</Label>
                  <Input value={zone} onChange={(e) => setZone(e.target.value)} placeholder="e.g. A or COLD" />
                </div>
                <div className="space-y-2">
                  <Label>Specific location</Label>
                  <Select value={locationId || "any"} onValueChange={(v) => setLocationId(v === "any" ? "" : v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      <SelectItem value="any">No specific location</SelectItem>
                      {locations.map((l: any) => <SelectItem key={l.id} value={l.id}>{l.code}{l.zone ? ` · ${l.zone}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Input type="number" value={priority} onChange={(e) => setPriority(e.target.value)} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
              </div>
              <DialogFooter><Button type="submit">Add rule</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border rounded-lg bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-16">Pri</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>SKU</TableHead>
            <TableHead>Zone</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Status</TableHead>
            <TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : rules.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No rules yet.</TableCell></TableRow>
            ) : rules.map((r: any) => (
              <TableRow key={r.id}>
                <TableCell className="font-mono text-xs">{r.priority}</TableCell>
                <TableCell className="text-sm">{customerName(r.customer_id)}</TableCell>
                <TableCell className="text-sm">{skuLabel(r.sku_id)}</TableCell>
                <TableCell className="text-sm">{r.zone ?? "—"}</TableCell>
                <TableCell className="text-sm flex items-center gap-1">
                  {r.location_id && <MapPin className="h-3 w-3 text-muted-foreground" />}
                  {locationCode(r.location_id)}
                </TableCell>
                <TableCell className="text-muted-foreground text-xs">{r.active ? "active" : "off"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => toggleActive(r.id, r.active)}>
                    {r.active ? "Disable" : "Enable"}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(r.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
